import React, { PropsWithChildren } from "react";
import { useAccount, useMsal } from "@azure/msal-react";
import {
  Body1,
  Button,
  Caption1,
  Divider,
  Title2,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { authConfig } from "../../model/auth";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
    backgroundImage: `radial-gradient(circle at top left, ${tokens.colorBrandBackground2}, transparent 28%)`,
  },
  container: {
    maxWidth: "1120px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: tokens.spacingVerticalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    display: "grid",
    gap: tokens.spacingVerticalXXL,
  },
  header: {
    display: "grid",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXL,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  brand: {
    display: "grid",
    gap: tokens.spacingVerticalXS,
  },
  accountPanel: {
    display: "grid",
    justifyItems: "end",
    gap: tokens.spacingVerticalS,
  },
  accountInfo: {
    display: "grid",
    gap: tokens.spacingVerticalXS,
    textAlign: "right",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
  },
  content: {
    display: "grid",
    gap: tokens.spacingVerticalXL,
  },
});

export const AppShell: React.FC<PropsWithChildren> = ({ children }) => {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const account = useAccount() ?? instance.getActiveAccount() ?? accounts[0];

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.brand}>
              <Title2>Mailmaister subscription workspace</Title2>
            </div>

            <div className={styles.accountPanel}>
              {account && (
                <div className={styles.accountInfo}>
                  <Body1>{account.name}</Body1>
                  <Caption1>{account.username}</Caption1>
                </div>
              )}
              <div className={styles.actionRow}>
                {!account && (
                  <Button appearance="primary" onClick={() => instance.loginRedirect(authConfig)}>
                    Sign in
                  </Button>
                )}
                {account && (
                  <>
                    <Button
                      appearance="secondary"
                      onClick={() => instance.loginRedirect({
                        ...authConfig,
                        prompt: "select_account"
                      })}
                    >
                      Switch account
                    </Button>
                    <Button appearance="subtle" onClick={() => instance.logoutRedirect()}>
                      Sign out
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <Divider />
        </header>

        <main className={styles.content}>
          {children}
        </main>
      </div>
    </div>
  );
};
